import React, { useState, useEffect, useRef } from 'react';
import { Check } from 'lucide-react';
import { useLanguage } from '../../contexts/useLanguage';
import { useApp } from '../../contexts/AppContext';
import { getCartItemCount } from '../../services/cartService';
import { webdesignedLayoutTier } from './core/resolveExperienceMode';
import {
  buildMonthDayCells,
  defaultBookableMonth,
  getDaysInMonth,
  getFirstDayOfMonth,
  isDateSelectable,
} from './shoppingCartAssistantCalendarUtils';
import { ShoppingCartAssistantGroupSection } from './ShoppingCartAssistantGroupSection';
import { ShoppingCartAssistantHeaderBar } from './ShoppingCartAssistantHeaderBar';
import { ShoppingCartAssistantTravelSection } from './ShoppingCartAssistantTravelSection';
import { UpdateTravelInfoDialog } from './UpdateTravelInfoDialog';
import { TravelMilestoneTracker, type TravelMilestoneStep } from './TravelMilestoneTracker';
import type { Group, ShoppingCartData } from './shoppingCartTypes';

const START_HERE_KEY = 'webdesigned_assistant_start_here_dismissed';

export type { Group, ShoppingCartData } from './shoppingCartTypes';

function sameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
}

function sameGroups(a: Group[], b: Group[] | undefined): boolean {
  const right = b ?? [];
  if (a.length !== right.length) return false;
  return a.every((g, i) => g.adults === right[i]?.adults && g.kids === right[i]?.kids);
}

interface ShoppingCartAssistantProps {
  isOpen: boolean;
  onToggle: () => void;
  onContinue?: (data: ShoppingCartData) => void;
  onMinimizeChange?: (isMinimized: boolean) => void;
  language: string;
  setLanguage: (lang: 'fr' | 'en' | 'zh') => void;
  isMinimizedProp?: boolean;
  /** Last saved travel info from cart context (dates + groups). */
  externalTravel?: ShoppingCartData | null;
  /** @deprecated use externalTravel */
  externalDates?: { startDate: Date | null; endDate: Date | null } | null;
  onCartClick?: () => void;
  topOffset?: number;
  transparentWhenUnhovered?: boolean;
  layoutMode?: 'phone' | 'tablet' | 'desktop';
}

const ShoppingCartAssistant: React.FC<ShoppingCartAssistantProps> = ({
  isOpen: _isOpen,
  onToggle: _onToggle,
  onContinue,
  onMinimizeChange,
  language,
  setLanguage,
  isMinimizedProp,
  externalTravel,
  externalDates,
  onCartClick,
  topOffset = 64,
  transparentWhenUnhovered: _transparentWhenUnhovered = true,
  layoutMode,
}) => {
  const { t } = useLanguage();
  const { addNotification } = useApp();
  const savedTravel = externalTravel ??
    (externalDates
      ? { startDate: externalDates.startDate, endDate: externalDates.endDate, groups: [] }
      : null);

  const [currentMonth, setCurrentMonth] = useState(() => defaultBookableMonth());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  /** Nights duration — adjustable before a start date is picked. */
  const [plannedNights, setPlannedNights] = useState(1);
  const [groups, setGroups] = useState<Group[]>([{ id: '1', adults: 2, kids: 0 }]);
  const [isTravelDurationExpanded, setIsTravelDurationExpanded] = useState(true);
  const [isGroupDetailsExpanded, setIsGroupDetailsExpanded] = useState(true);
  const [internalMinimized, setInternalMinimized] = useState(true);
  const [cartItemCount, setCartItemCount] = useState(0);
  const [isLanguageQuickOpen, setIsLanguageQuickOpen] = useState(false);
  const [showUpdateTravelDialog, setShowUpdateTravelDialog] = useState(false);
  const [milestoneStep, setMilestoneStep] = useState<TravelMilestoneStep>(1);
  const [showStartHere, setShowStartHere] = useState(() => {
    if (typeof sessionStorage === 'undefined') return false;
    try {
      return sessionStorage.getItem(START_HERE_KEY) !== '1';
    } catch {
      return true;
    }
  });
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const languageMenuRef = useRef<HTMLDivElement | null>(null);
  const languageTriggerRef = useRef<HTMLButtonElement | null>(null);
  const panelScrollRef = useRef<HTMLDivElement | null>(null);
  const languageOptions: Array<{ code: 'fr' | 'en' | 'zh'; label: string }> = [
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'zh', label: 'ZH' },
  ];
  const isLanguageMenuVisible = isLanguageQuickOpen;

  useEffect(() => {
    if (!isLanguageQuickOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (languageMenuRef.current?.contains(target)) return;
      if (languageTriggerRef.current?.contains(target)) return;
      setIsLanguageQuickOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isLanguageQuickOpen]);

  const computedLayoutMode: 'phone' | 'tablet' | 'desktop' =
    layoutMode ?? webdesignedLayoutTier(viewportWidth);
  const isPhone = computedLayoutMode === 'phone';
  const isTablet = computedLayoutMode === 'tablet';
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Keep duration + groups open so both are visible at a glance (all layouts).
  useEffect(() => {
    setIsTravelDurationExpanded(true);
    setIsGroupDetailsExpanded(true);
  }, []);

  const isMinimized = isMinimizedProp !== undefined ? isMinimizedProp : internalMinimized;

  useEffect(() => {
    if (isMinimizedProp !== undefined) {
      setInternalMinimized(isMinimizedProp);
    }
  }, [isMinimizedProp]);

  /** Hydrate local editor from last saved travel info when opening / when saved data changes. */
  useEffect(() => {
    if (isMinimized) return;
    if (!savedTravel) {
      // No saved trip yet — show the current bookable month (not a hardcoded January).
      if (!selectedStartDate) setCurrentMonth(defaultBookableMonth());
      return;
    }
    setSelectedStartDate(savedTravel.startDate);
    setSelectedEndDate(savedTravel.endDate);
    if (savedTravel.startDate && savedTravel.endDate) {
      const nights = Math.max(
        1,
        Math.round(
          (savedTravel.endDate.getTime() - savedTravel.startDate.getTime()) / (1000 * 60 * 60 * 24),
        ),
      );
      setPlannedNights(nights);
    }
    if (savedTravel.groups?.length) {
      setGroups(savedTravel.groups.map((g) => ({ ...g })));
    }
    if (savedTravel.startDate) {
      setCurrentMonth(
        new Date(savedTravel.startDate.getFullYear(), savedTravel.startDate.getMonth(), 1)
      );
    } else {
      setCurrentMonth(defaultBookableMonth());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when sheet opens or saved snapshot identity changes
  }, [
    isMinimized,
    savedTravel?.startDate?.getTime(),
    savedTravel?.endDate?.getTime(),
    JSON.stringify(savedTravel?.groups ?? []),
  ]);

  useEffect(() => {
    const loadCartCount = () => {
      const count = getCartItemCount();
      setCartItemCount(count);
    };

    loadCartCount();

    const handleCartUpdate = () => {
      loadCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Phone sheet covers most of the viewport — lock background scroll.
  // Pad/desktop is a left rail; the rest of the page must stay scrollable.
  useEffect(() => {
    if (isMinimized || !isPhone) return;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverscroll = html.style.overscrollBehavior;
    const prevBodyOverscroll = body.style.overscrollBehavior;

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.style.overscrollBehavior = prevHtmlOverscroll;
      body.style.overscrollBehavior = prevBodyOverscroll;
    };
  }, [isMinimized, isPhone]);

  const applyEndFromStart = (start: Date, nights: number) => {
    const end = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    end.setDate(end.getDate() + nights);
    return end;
  };

  const handlePlannedNightsChange = (nights: number) => {
    const next = Math.max(1, nights);
    setPlannedNights(next);
    if (selectedStartDate) {
      setSelectedEndDate(applyEndFromStart(selectedStartDate, next));
    }
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    if (!isDateSelectable(clickedDate)) return;

    // Two-click period: 1) first day  2) last day. Duration stepper can also set the end.
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // New period — choose first day only.
      setSelectedStartDate(clickedDate);
      setSelectedEndDate(null);
      return;
    }

    // Second click — complete the period.
    if (clickedDate.getTime() === selectedStartDate.getTime()) {
      // Same day → 1 night (checkout next day).
      setPlannedNights(1);
      setSelectedEndDate(applyEndFromStart(clickedDate, 1));
      return;
    }
    if (clickedDate < selectedStartDate) {
      const nights = Math.max(
        1,
        Math.round(
          (selectedStartDate.getTime() - clickedDate.getTime()) / (1000 * 60 * 60 * 24),
        ),
      );
      setPlannedNights(nights);
      setSelectedEndDate(selectedStartDate);
      setSelectedStartDate(clickedDate);
      return;
    }
    const nights = Math.max(
      1,
      Math.round((clickedDate.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24)),
    );
    setPlannedNights(nights);
    setSelectedEndDate(clickedDate);
  };

  useEffect(() => {
    if (selectedStartDate) {
      const startMonth = new Date(selectedStartDate.getFullYear(), selectedStartDate.getMonth(), 1);
      const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      if (startMonth.getTime() !== currentMonthStart.getTime()) {
        setCurrentMonth(startMonth);
      }
    }
  }, [selectedStartDate]);

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const defaultGroups: Group[] = [{ id: '1', adults: 2, kids: 0 }];
  const isDirty = savedTravel
    ? !sameDay(selectedStartDate, savedTravel.startDate) ||
      !sameDay(selectedEndDate, savedTravel.endDate) ||
      !sameGroups(groups, savedTravel.groups)
    : !!(selectedStartDate || selectedEndDate) || !sameGroups(groups, defaultGroups);

  const forceClose = () => {
    setShowUpdateTravelDialog(false);
    if (isMinimizedProp === undefined) {
      setInternalMinimized(true);
    }
    onMinimizeChange?.(true);
  };

  const persistTravelInfo = () => {
    if (!(selectedStartDate && selectedEndDate && onContinue)) return false;
    onContinue({
      startDate: selectedStartDate,
      endDate: selectedEndDate,
      groups,
    });
    addNotification({
      type: 'success',
      message: t('shoppingCart.bookingConditionsUpdated'),
      duration: 6000,
    });
    return true;
  };

  const handleContinue = () => {
    if (persistTravelInfo()) forceClose();
  };

  const totalAdults = groups.reduce((sum, group) => sum + group.adults, 0);
  const totalKids = groups.reduce((sum, group) => sum + group.kids, 0);
  const isFormValid = !!(selectedStartDate && selectedEndDate && groups.length > 0 && totalAdults > 0);

  const handleMinimizeToggle = (nextMinimized: boolean) => {
    if (!nextMinimized) {
      if (isMinimizedProp === undefined) setInternalMinimized(false);
      onMinimizeChange?.(false);
      return;
    }
    // Back always leaves. Prompt only when there is a complete draft worth saving.
    if (isDirty && isFormValid) {
      setShowUpdateTravelDialog(true);
      return;
    }
    forceClose();
  };

  const addGroup = () => {
    const newId = String(groups.length + 1);
    setGroups([...groups, { id: newId, adults: 2, kids: 0 }]);
  };

  const removeGroup = (groupId: string) => {
    if (groups.length > 1) {
      setGroups(groups.filter((g) => g.id !== groupId));
    }
  };

  const updateGroup = (groupId: string, field: 'adults' | 'kids', value: number) => {
    setGroups(
      groups.map((g) =>
        g.id === groupId ? { ...g, [field]: Math.max(field === 'adults' ? 1 : 0, value) } : g
      )
    );
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const firstDay = getFirstDayOfMonth(currentMonth);
  const monthName = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const days = buildMonthDayCells(daysInMonth, firstDay);

  const headerBar = (headerEffectiveHovered: boolean) => (
    <ShoppingCartAssistantHeaderBar
      t={t}
      isMinimized={isMinimized}
      isPhone={isPhone}
      effectiveHovered={headerEffectiveHovered}
      topOffset={topOffset}
      handleMinimizeToggle={handleMinimizeToggle}
      onCartClick={onCartClick}
      cartItemCount={cartItemCount}
      language={language}
      setLanguage={setLanguage}
      languageTriggerRef={languageTriggerRef}
      languageMenuRef={languageMenuRef}
      isLanguageQuickOpen={isLanguageQuickOpen}
      setIsLanguageQuickOpen={setIsLanguageQuickOpen}
      isLanguageMenuVisible={isLanguageMenuVisible}
      languageOptions={languageOptions}
    />
  );

  /** Checkmarks only after travel info is saved — not for draft edits. */
  const durationSaved = !!(savedTravel?.startDate && savedTravel?.endDate);
  const groupsSaved =
    !!savedTravel?.groups?.length &&
    savedTravel.groups.reduce((sum, g) => sum + g.adults, 0) > 0 &&
    durationSaved;

  const durationLocal = !!(selectedStartDate && selectedEndDate);

  // Stay on step 1 after dates are chosen — advance only via Next / tracker click.
  useEffect(() => {
    if (!durationLocal) setMilestoneStep(1);
  }, [durationLocal]);

  // Reset panel scroll when switching milestones so nested content doesn't leave a stuck offset.
  useEffect(() => {
    const el = panelScrollRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: 'auto' });
  }, [milestoneStep]);

  const dismissStartHere = () => {
    setShowStartHere(false);
    try {
      sessionStorage.setItem(START_HERE_KEY, '1');
    } catch {
      /* ignore */
    }
  };

  const expandedBody = !isMinimized ? (
    <>
      <div
        ref={panelScrollRef}
        className={`min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-y-contain touch-pan-y bg-[#ece3de] [-webkit-overflow-scrolling:touch] ${
          isPhone ? 'p-4' : 'p-3'
        }`}
      >
        <TravelMilestoneTracker
          step={milestoneStep}
          durationDone={durationSaved}
          groupsDone={groupsSaved}
          durationLabel={t('shoppingCart.milestoneDuration') || 'Duration'}
          groupsLabel={t('shoppingCart.milestoneGroups') || 'Groups'}
          onSelectStep={(s) => {
            if (s === 2 && !durationLocal) return;
            setMilestoneStep(s);
            setIsTravelDurationExpanded(true);
            setIsGroupDetailsExpanded(true);
          }}
        />

        {milestoneStep === 1 ? (
          <ShoppingCartAssistantTravelSection
            t={t}
            isTravelDurationExpanded={isTravelDurationExpanded}
            setIsTravelDurationExpanded={setIsTravelDurationExpanded}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedStartDate={setSelectedStartDate}
            setSelectedEndDate={setSelectedEndDate}
            plannedNights={plannedNights}
            onPlannedNightsChange={handlePlannedNightsChange}
            currentMonth={currentMonth}
            groups={groups}
            onContinue={onContinue}
            handlePrevMonth={handlePrevMonth}
            handleNextMonth={handleNextMonth}
            handleDateClick={handleDateClick}
            monthName={monthName}
            days={days}
          />
        ) : (
          <ShoppingCartAssistantGroupSection
            t={t}
            isGroupDetailsExpanded={isGroupDetailsExpanded}
            setIsGroupDetailsExpanded={setIsGroupDetailsExpanded}
            groups={groups}
            addGroup={addGroup}
            removeGroup={removeGroup}
            updateGroup={updateGroup}
            totalAdults={totalAdults}
            totalKids={totalKids}
          />
        )}
      </div>

      <div className="flex-shrink-0 space-y-2 border-t border-black/10 bg-[#e8ddd7]/80 p-3">
        {milestoneStep === 1 ? (
          <button
            type="button"
            onClick={() => durationLocal && setMilestoneStep(2)}
            disabled={!durationLocal}
            className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
              durationLocal
                ? 'bg-[#2f9e44] text-white hover:bg-[#27863a]'
                : 'cursor-not-allowed bg-[#3d2817]/15 text-[#3d2817]/40'
            }`}
          >
            {t('common.next') || 'Next'}
          </button>
        ) : (
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setMilestoneStep(1)}
              className="w-full rounded-xl border border-[#3d2817]/15 bg-white/80 px-4 py-2.5 text-sm font-semibold text-[#3d2817] transition-colors hover:bg-white"
            >
              {t('common.goBack') || 'Back'}
            </button>
            <button
              type="button"
              onClick={handleContinue}
              disabled={!isFormValid}
              className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                isFormValid
                  ? 'bg-[#2f9e44] text-white hover:bg-[#27863a]'
                  : 'cursor-not-allowed bg-[#3d2817]/15 text-[#3d2817]/40'
              }`}
            >
              <Check className="h-4 w-4 shrink-0" aria-hidden />
              <span className="text-center leading-snug">
                {t('common.confirm') || 'Confirm'}
              </span>
            </button>
          </div>
        )}
        {milestoneStep === 2 ? (
          <p className="text-center text-[11px] leading-relaxed text-[#5d4437]">
            {t('shoppingCart.continueDescription')}
          </p>
        ) : null}
      </div>
    </>
  ) : null;

  /** No left vertical rail — dock / Plan your visit opens the floating panel. */
  if (isMinimized) {
    return null;
  }

  const updateDialog = (
    <UpdateTravelInfoDialog
      open={showUpdateTravelDialog}
      canSave={!!isFormValid}
      onStay={() => setShowUpdateTravelDialog(false)}
      onDiscard={forceClose}
      onUpdate={() => {
        if (persistTravelInfo()) forceClose();
      }}
    />
  );

  // Floating cream panel on all layouts (phone inset so dock + language stay usable).
  const panelWidthClass = isPhone
    ? 'left-2 right-2 w-auto'
    : isTablet
      ? 'left-3 w-[22rem]'
      : 'left-4 w-[26rem]';
  // Phone: clear floating dock. Pad/desktop: align with dock baseline.
  const panelBottom = isPhone
    ? 'calc(env(safe-area-inset-bottom, 0px) + 5.25rem)'
    : isTablet
      ? 'calc(env(safe-area-inset-bottom, 0px) + 8px)'
      : '2rem';

  return (
    <>
      <div
        data-keep-web-nav-open="true"
        className={`fixed z-[10020] flex flex-col overflow-hidden rounded-2xl border border-black/15 bg-[#ece3de] shadow-2xl ${panelWidthClass}`}
        style={{
          top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
          bottom: panelBottom,
        }}
      >
        {headerBar(true)}
        {expandedBody}
        {showStartHere ? (
          isPhone ? (
            <div className="pointer-events-none absolute inset-x-3 top-[4.5rem] z-[90]">
              <div className="pointer-events-auto rounded-2xl border border-[#2f9e44]/35 bg-white px-3 py-2.5 shadow-xl">
                <p className="text-xs font-bold text-[#2f9e44]">
                  {t('shoppingCart.startHereStep1') || 'Start here · Step 1'}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-[#3d2817]/80">
                  {t('shoppingCart.startHereStep1Desc')}
                </p>
                <button
                  type="button"
                  onClick={dismissStartHere}
                  className="mt-2 text-[11px] font-semibold text-[#3d2817] underline-offset-2 hover:underline"
                >
                  {t('common.close') || 'Close'}
                </button>
              </div>
            </div>
          ) : (
            <div className="pointer-events-none absolute top-14 right-[-0.5rem] z-[90] translate-x-full pl-2 sm:right-[-0.25rem]">
              <div className="pointer-events-auto relative max-w-[14rem] rounded-2xl border border-[#2f9e44]/35 bg-white px-3 py-2.5 shadow-xl">
                <span
                  className="absolute top-4 -left-1.5 h-3 w-3 rotate-45 border-b border-l border-[#2f9e44]/35 bg-white"
                  aria-hidden
                />
                <p className="text-xs font-bold text-[#2f9e44]">
                  {t('shoppingCart.startHereStep1') || 'Start here · Step 1'}
                </p>
                <p className="mt-1 text-[11px] leading-snug text-[#3d2817]/80">
                  {t('shoppingCart.startHereStep1Desc')}
                </p>
                <button
                  type="button"
                  onClick={dismissStartHere}
                  className="mt-2 text-[11px] font-semibold text-[#3d2817] underline-offset-2 hover:underline"
                >
                  {t('common.close') || 'Close'}
                </button>
              </div>
            </div>
          )
        ) : null}
      </div>
      {updateDialog}
    </>
  );
};

export default ShoppingCartAssistant;
